// Global Instructions Rule Applied!
//
// PR #57 — Stripo HTML import affordance.
//
// Two-input modal for bringing a complete HTML document into the
// compose form: paste textarea + .html / .htm file upload. The
// document is dropped into the campaign's body_html verbatim with
// body_source='imported' (controller + dispatch bypass sanitize and
// the render.ts thin shell for this case).
//
// Operator workflow (architect approved 2026-05-29, see
// .claude/STRIPO_IMPORT_NOTES.md §7):
//   1. Operator designs the email in Stripo (or any external builder).
//   2. Stripo Export → HTML → the operator downloads the .html file
//      OR copies the HTML to the clipboard.
//   3. Compose form → click "Import HTML" → modal opens.
//   4. Upload the file OR paste the HTML.
//   5. Live preview in the iframe at the right shows what the
//      recipient will see (verbatim — no shell wrap).
//   6. "Use this body" → form values updated; modal closes; operator
//      clicks Save → row persisted with body_source='imported'.
//
// Re-import (architect decision §12.4 — re-import only, no in-app
// editing): when a campaign is already imported, the form replaces
// Jodit with a read-only iframe viewer + "Re-import" button which
// re-opens this same modal. Stripo CDN image URLs ride along (image
// strategy A, §5).
//
// Trust model (architect decision §12.2 — Option A): imported HTML
// bypasses sanitize on save AND on dispatch. The trust boundary is
// the operator's account credentials. A malicious operator can ship
// arbitrary HTML; the same operator already has send_as access, so
// the practical threat surface doesn't grow.

import React, { useCallback, useRef, useState } from 'react'
import { Alert, Button, Modal, Space, Tabs, Typography, Upload } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface'

const { Text, Paragraph } = Typography

export interface ImportHtmlModalProps {
  open:        boolean
  initialHtml?: string
  onSubmit:    (html: string) => void
  onCancel:    () => void
}

// Modest cap on imported document size. Stripo HTML exports for
// LP-style updates are typically 30–80 KB. 5 MB gives a generous
// ceiling without risking a runaway DOM in the preview iframe or
// later runaway message size at queue time (queue_email_campaign
// already has its own 7 MB raw cap).
const MAX_IMPORT_BYTES = 5 * 1024 * 1024

export default function ImportHtmlModal({
  open,
  initialHtml = '',
  onSubmit,
  onCancel
}: ImportHtmlModalProps) {
  const [html, setHtml] = useState<string>(initialHtml)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'upload' | 'paste'>('upload')
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeHeight, setIframeHeight] = useState<number>(400)

  // Re-seed when the parent re-opens with a different initial value
  // (e.g. Re-import flow on an already-imported campaign).
  React.useEffect(() => {
    if (open) {
      setHtml(initialHtml || '')
      setError(null)
      setActiveTab(initialHtml ? 'paste' : 'upload')
    }
  }, [open, initialHtml])

  const handleFileBeforeUpload: UploadProps['beforeUpload'] = useCallback(
    (file: RcFile) => {
      // Drag-drop / file picker handler. We never actually upload to
      // a server — the FileReader reads the bytes client-side, then
      // we hand the operator's submit click off to the form save.
      // Returning false tells AntD to skip its upload pipeline.
      if (!file.name.match(/\.(html?|HTML?)$/)) {
        setError(`"${file.name}" doesn't look like an HTML file (.html / .htm).`)
        return false
      }
      if (file.size > MAX_IMPORT_BYTES) {
        setError(
          `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB; the import cap is ${
            MAX_IMPORT_BYTES / 1024 / 1024
          } MB.`
        )
        return false
      }
      const reader = new FileReader()
      reader.onload = () => {
        const text = String(reader.result || '')
        setHtml(text)
        setError(null)
        setActiveTab('paste')
      }
      reader.onerror = () => {
        setError(`Couldn't read "${file.name}": ${reader.error?.message || 'unknown error'}`)
      }
      reader.readAsText(file)
      return false
    },
    []
  )

  const handleIframeLoad = useCallback(() => {
    const f = iframeRef.current
    const doc = f?.contentDocument
    if (!f || !doc || !doc.body) return
    setIframeHeight(Math.max(240, doc.body.scrollHeight + 4))
  }, [])

  const handleSubmit = useCallback(() => {
    const trimmed = (html || '').trim()
    if (!trimmed) {
      setError('Paste HTML or upload a file before importing.')
      return
    }
    onSubmit(trimmed)
  }, [html, onSubmit])

  const previewSrcDoc = html || '<p style="color:#bfbfbf;font-family:sans-serif;padding:24px">(no HTML imported yet)</p>'

  // Upload component config — single file at a time, no list display
  // (we handle the parsed text inline rather than tracking files).
  const uploadProps: UploadProps = {
    accept: '.html,.htm',
    multiple: false,
    showUploadList: false,
    beforeUpload: handleFileBeforeUpload,
    fileList: [] as UploadFile[]
  }

  return (
    <Modal
      title='Import HTML body'
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText='Use this body'
      cancelText='Cancel'
      width={1100}
      destroyOnClose
      bodyStyle={{ paddingTop: 12 }}
    >
      <Paragraph type='secondary' style={{ marginBottom: 12 }}>
        Paste a complete HTML document (e.g. from Stripo's Export → HTML)
        or upload an <Text code>.html</Text> file. The body is stored
        verbatim and sent to recipients exactly as previewed — no
        sanitizer rewrite, no shell wrap. Make sure the HTML comes from a
        source you trust.
      </Paragraph>

      {error && (
        <Alert
          type='error'
          showIcon
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div>
          <Tabs
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k as 'upload' | 'paste')}
            size='small'
            items={[
              {
                key: 'upload',
                label: 'Upload file',
                children: (
                  <Upload.Dragger {...uploadProps} style={{ padding: 16 }}>
                    <p className='ant-upload-drag-icon'>
                      <InboxOutlined />
                    </p>
                    <p className='ant-upload-text'>Click or drag an .html / .htm file here</p>
                    <p className='ant-upload-hint' style={{ fontSize: 12 }}>
                      Single file. Up to {MAX_IMPORT_BYTES / 1024 / 1024} MB.
                    </p>
                  </Upload.Dragger>
                )
              },
              {
                key: 'paste',
                label: 'Paste HTML',
                children: (
                  <textarea
                    value={html}
                    onChange={(e) => {
                      setHtml(e.target.value)
                      setError(null)
                    }}
                    placeholder='<!doctype html>\n<html>\n  <head>...</head>\n  <body>...</body>\n</html>'
                    style={{
                      width:        '100%',
                      minHeight:    320,
                      fontFamily:   'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize:     12,
                      padding:      8,
                      border:       '1px solid #d9d9d9',
                      borderRadius: 4,
                      resize:       'vertical'
                    }}
                  />
                )
              }
            ]}
          />
          <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c' }}>
            <Space size={6} wrap>
              <Text type='secondary'>Tip:</Text>
              <Text type='secondary'>
                Stripo's HTML export is a complete document with its own
                {' '}<Text code>&lt;html&gt;</Text> / <Text code>&lt;head&gt;</Text> / <Text code>&lt;body&gt;</Text> —
                paste or upload it as-is. Merge fields like
                {' '}<Text code>{`{{first_name}}`}</Text> work the same way as in the Jodit editor.
              </Text>
            </Space>
          </div>
        </div>

        <div>
          <div style={{ marginBottom: 6, fontSize: 12, color: '#8c8c8c' }}>Preview</div>
          {/* Same iframe sandbox shape as PreviewPane (allow-same-origin
              so the parent can read scrollHeight; no allow-scripts so
              imported HTML can't run JS in our origin). */}
          <iframe
            ref={iframeRef}
            title='Imported HTML preview'
            srcDoc={previewSrcDoc}
            onLoad={handleIframeLoad}
            sandbox='allow-same-origin'
            style={{
              width:        '100%',
              height:       iframeHeight,
              maxHeight:    600,
              border:       '1px solid #d9d9d9',
              borderRadius: 4,
              background:   '#ffffff',
              display:      'block'
            }}
          />
        </div>
      </div>
    </Modal>
  )
}
